import { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import Loading from '@/components/general/Loading';
import jobPagesContext from '@/contexts/jobPagesContext';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import jobServices, { NewTopicParams } from '@/services/jobs';
import InputText from '@/trio-ui/InputText';
import Modal from '@/trio-ui/Modal';
import { onScrollToSmoothly } from '@/utils/global';
import JobNotesComponent from './JobNotes.component';

const defaultNewTopicToAdd = {
  name: '',
  isModalOpen: false,
};

const JobNotes = function (): JSX.Element {
  const { jobId } = useParams();
  const toast = useToastContext();
  const { job, refetchJob, isLoadingJob } = useContext(jobPagesContext);

  const [newTopicToAdd, setNewTopicToAdd] = useState(defaultNewTopicToAdd);

  const addNewTopicMutation = useApiSubmit<any, NewTopicParams>(jobServices.addNewTopic);

  const onAddNewNote = (): void => {
    refetchJob();
  };

  const onAddNewTopic = async (): Promise<void> => {
    try {
      const { name } = newTopicToAdd;

      await addNewTopicMutation.mutateAsync({
        jobId: jobId as string,
        name,
      });

      setNewTopicToAdd(defaultNewTopicToAdd);
      refetchJob();
      toast.showSuccessFeedback('The new topic has been added.');
    } catch (err) {
      setNewTopicToAdd(defaultNewTopicToAdd);
      toast.showErrorFeedback('There was a problem adding the new topic.');
    }
  };

  const onChangeTopicName = (event): void => {
    const {
      target: { value },
    } = event;

    setNewTopicToAdd((prev) => ({
      ...prev,
      name: value,
    }));
  };

  const onCloseModal = (): void => {
    setNewTopicToAdd(defaultNewTopicToAdd);
  };

  const onOpenModal = (): void => {
    setNewTopicToAdd({ ...defaultNewTopicToAdd, isModalOpen: true });
  };

  const jobNoteTopics = job?.topics ?? [];
  const notes = job?.notes ?? [];

  const topicsWithNotes = jobNoteTopics.map((topic) => {
    const notesOfCurrentTopic = notes.filter((note) => note.topic.id === topic?.id);

    return {
      id: topic?.id ?? '',
      title: topic?.name ?? '',
      notes: notesOfCurrentTopic,
    };
  });

  if (isLoadingJob) {
    return <Loading fullPage />;
  }

  return (
    <>
      <JobNotesComponent
        onScrollTo={onScrollToSmoothly}
        onAddNewTopic={onOpenModal}
        onAddNewNote={onAddNewNote}
        topics={topicsWithNotes}
      />
      <Modal
        modalId="new-topic-modal"
        isCentered
        size="sm"
        onConfirmClick={onAddNewTopic}
        modalTitle="Add a new topic"
        onConfirmLabel="Add the new topic"
        isButtonDisabled={newTopicToAdd?.name?.length <= 3}
        bodyRender={
          <InputText
            isLabelAboveField
            label="Topic Name"
            name="new-topic"
            value={newTopicToAdd.name}
            onChangeHandler={onChangeTopicName}
          />
        }
        onClose={onCloseModal}
        isOpen={newTopicToAdd.isModalOpen}
      />
    </>
  );
};

export default JobNotes;
